/**
 * Utility functions for date and time formatting
 */

/**
 * Format date to user-friendly format
 * @param dateString - The date string to format
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  try {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  } catch (error) {
    return dateString || 'N/A';
  }
};

/**
 * Format date without time components
 * @param dateString - The date string to format
 * @returns Formatted date string (month, day, year)
 */
export const formatDateOnly = (dateString: string): string => {
  try {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return dateString || 'N/A';
  }
};

/**
 * Format time only
 * @param dateString - The date string to format
 * @returns Formatted time string (hour:minute AM/PM)
 */
export const formatTimeOnly = (dateString: string): string => {
  try {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  } catch (error) {
    return dateString || 'N/A';
  }
};

/**
 * Format duration in MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export const formatDuration = (seconds: number): string => {
  try {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  } catch (error) {
    return `${seconds}s`;
  }
}; 

/**
 * Format time string to MM:SS format
 * @param time - Time string in either HH:MM:SS.ms or MM:SS.ms format
 * @returns Formatted time string in MM:SS format
 */
export const formatTimeString = (time: string): string => {
  try {
    // Split time into parts
    const parts = time.split(':');
    if (parts.length < 2) return time;

    // Get minutes and seconds
    const minutes = parts[parts.length - 2];
    const seconds = parts[parts.length - 1].split('.')[0]; // Remove milliseconds

    return `${minutes}:${seconds}`;
  } catch (error) {
    return time;
  }
};